import { useEffect, useState } from "react";

/**
 * CatalogConvert Logo — Science Final A, micro-optimized
 *
 * Icon values (locked):
 *   rx=22, bars y=21/45/69, h=10, rx=5
 *   middle bar opacity=0.55
 *   gradient #2952D8 → #6B9BFF (45°)
 *
 * Wordmark values (locked):
 *   "catalog": weight=400, color=#1E293B (light) / #CBD5E1 (dark), tracking=+0.03em
 *   "convert": weight=800, gradient ≥14px / solid below, tracking=-0.04em ≥14px / -0.02em below
 *
 * Props:
 *   size        — controls icon size; text scales proportionally (default: 32)
 *   iconSize    — pin icon size independently
 *   dark        — force dark wordmark (auto-detected via data-theme by default)
 *   onClick     — makes wrapper clickable
 *   iconOnly    — renders icon without wordmark
 *   className   — passed to outer wrapper
 */

const GRAD_ID_COUNTER = { n: 0 };

export default function Logo({
  size = 32,
  iconSize,
  dark,
  onClick,
  iconOnly = false,
  className = "",
}: any) {
  const [isDark, setIsDark] = useState(false);

  // Auto-detect dark mode via data-theme attribute
  useEffect(() => {
    const check = () =>
      setIsDark(
        document.documentElement.getAttribute("data-theme") === "dark"
      );
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const useDark = dark !== undefined ? dark : isDark;

  // Unique gradient ID per instance to avoid SVG defs collision
  const [gradId] = useState(() => {
    GRAD_ID_COUNTER.n += 1;
    return `cc-logo-grad-${GRAD_ID_COUNTER.n}`;
  });

// fontSize = 56.25% of size, matches original proportions
const fontSize = Math.round(size * 0.5625);
const iSize = iconSize ?? Math.round(fontSize * 1.25);
const gap = Math.round(iSize * 0.35);

  // Wordmark "convert" — gradient at ≥14px, solid below
  const convertUsesGradient = fontSize >= 14;

  // "convert" tracking — tight at large, looser at small
  const convertTracking = fontSize >= 14 ? "-0.04em" : "-0.02em";

  const catalogColor = useDark ? "#CBD5E1" : "#1E293B";
  const convertSolidColor = useDark ? "#6B9BFF" : "#2952D8";

  const wrapperStyle: any = {
    display: "inline-flex",
    alignItems: "center",
    gap: `${gap}px`,
    cursor: onClick ? "pointer" : "default",
    userSelect: "none",
    textDecoration: "none",
  };

  const wordmarkStyle = {
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: `${fontSize}px`,
    lineHeight: 1,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  };

  const catalogStyle = {
    fontWeight: 300,
    color: catalogColor,
    letterSpacing: "0.03em",
  };

  const convertGradientStyle = {
    fontWeight: 800,
    letterSpacing: convertTracking,
    background: "linear-gradient(135deg, #2952D8, #6B9BFF)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  };

  const convertSolidStyle = {
    fontWeight: 800,
    letterSpacing: convertTracking,
    color: convertSolidColor,
  };

  return (
    <div
      style={wrapperStyle}
      className={className}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => (e.key === "Enter" || e.key === " ") && onClick(e)
          : undefined
      }
    >
      {/* Icon */}
      <svg
        width={iSize}
        height={iSize}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#2952D8" />
            <stop offset="100%" stopColor="#6B9BFF" />
          </linearGradient>
        </defs>
        {/* Background tile */}
        <rect width="100" height="100" rx="22" fill={`url(#${gradId})`} />
        {/* Bar 1 — full width, top */}
        <rect x="28" y="21" width="44" height="10" rx="5" fill="white" />
        {/* Bar 2 — half width, middle, muted */}
        <rect
          x="28"
          y="45"
          width="22"
          height="10"
          rx="5"
          fill="white"
          opacity="0.55"
        />
        {/* Bar 3 — full width, bottom */}
        <rect x="28" y="69" width="44" height="10" rx="5" fill="white" />
      </svg>

      {/* Wordmark */}
      {!iconOnly && (
        <span style={wordmarkStyle} aria-label="CatalogConvert">
          <span style={catalogStyle}>catalog</span>
          <span
            style={
              convertUsesGradient ? convertGradientStyle : convertSolidStyle
            }
          >
            convert
          </span>
        </span>
      )}
    </div>
  );
}
